import { create } from "zustand";
import { TrainingState, IntervalSettings, Position } from "../types";

interface TrainingStore extends TrainingState {
  settings: IntervalSettings;
  setSettings: (settings: IntervalSettings) => void;
  startTraining: () => void;
  pauseTraining: () => void;
  resumeTraining: () => void;
  stopTraining: () => void;
  tick: () => void;
  switchPhase: () => void;
  addPosition: (position: Position) => void;
  updateDistance: (distance: number) => void;
}

const initialState: TrainingState = {
  isActive: false,
  isPaused: false,
  currentPhase: "running",
  elapsedSeconds: 0,
  phaseElapsedSeconds: 0,
  positions: [],
  totalDistance: 0,
};

export const useTrainingStore = create<TrainingStore>((set, get) => ({
  ...initialState,
  settings: { runMinutes: 1, runSeconds: 0, walkMinutes: 1, walkSeconds: 0 },
  setSettings: (settings) => set({ settings }),
  startTraining: () => set({ ...initialState, isActive: true }),
  pauseTraining: () => set({ isPaused: true }),
  resumeTraining: () => set({ isPaused: false }),
  stopTraining: () => set({ isActive: false, isPaused: false }),
  tick: () => {
    const state = get();
    if (!state.isActive || state.isPaused) return;
    const { settings, currentPhase, phaseElapsedSeconds } = state;
    const phaseDuration =
      currentPhase === "running"
        ? settings.runMinutes * 60 + settings.runSeconds
        : settings.walkMinutes * 60 + settings.walkSeconds;

    if (phaseElapsedSeconds + 1 >= phaseDuration) {
      get().switchPhase();
    } else {
      set({
        elapsedSeconds: state.elapsedSeconds + 1,
        phaseElapsedSeconds: phaseElapsedSeconds + 1,
      });
    }
  },
  switchPhase: () =>
    set((state) => ({
      currentPhase: state.currentPhase === "running" ? "walking" : "running",
      phaseElapsedSeconds: 0,
      elapsedSeconds: state.elapsedSeconds + 1,
    })),
  addPosition: (position) =>
    set((state) => ({ positions: [...state.positions, position] })),
  updateDistance: (distance) => set({ totalDistance: distance }),
}));
